import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRightAlt,
  Hotel,
  DateRange,
  Flight,
  MoreVert,
  SwapHoriz,
  Person,
  ThumbUp,
  Tune,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  List,
  ListItem,
  Typography,
  useTheme,
  styled,
  Skeleton,
} from '@mui/material';

import { useConfirmFareMutation } from '@frontend/features/flight/queries';
import { FareSummary } from '@frontend/features/flight/components/FareSummary';
import { PriceTrend } from '@frontend/features/common/components/PriceTrend';
import { FlightsRequestQueryParams, FlightsRequestPayload } from '@frontend/features/common/types';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { useRockBottom } from '@frontend/features/common/hooks/useRockBottom';
import { MobileAvailabilityLayout } from '@frontend/features/common/components/AvailabilityLayout/MobileAvailabilityLayout';
import { BackToolbar } from '@frontend/features/common/components/BackToolbar';
import { LuggageIndicator } from '@frontend/features/common/components/LuggageIndicator';
import { StopsIndicator } from '@frontend/features/common/components/StopsIndicator';
import { ItineraryDto } from '@frontend/features/flight/types';

import { routes } from '../routes';
import { returnAirlineLogoUrl } from '@frontend/features/flight/utils';
import { CONFIG } from '@frontend/config';

type StupidFuckingHack = Record<string, ItineraryDto[]>;

const CustomCard = styled(Card)`
  border-radius: 16px;
  width: 100%;
  p {
  }
`;

// TODO: Una descarada copia de lo que esta hecho de hotels.
// Habria que desacoplarlo y hacerlo mas generico para usarlo
// en ambos hotels y flights.
export const FlightsAvailability = () => {
  const { isConnected, socketRef, sendMessageDebounced: sendMessage } = useWS(`${CONFIG.WS_URL}/flights`);

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // TODO: Considerar loading state para cuando el client hace click
  // en uno de los fares de la lista y empieza el proceso de confirmacion,
  // que pasa con ese loading state y el resto de los fares.
  const {
    mutate: mutateConfirmFare,
    isLoading: isFareConfirmLoading,
    data: fareConfirmData,
    isError: isFareConfirmError,
  } = useConfirmFareMutation();

  const queryParams = useMemo<FlightsRequestQueryParams>(() => {
    const params = new URLSearchParams(location.search);
    const flightDataQueryParams = params.get('config');
    return flightDataQueryParams ? JSON.parse(decodeURIComponent(flightDataQueryParams)) : null;
  }, [location.search]);

  const [openEdit, setOpenEdit] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedFare, setSelectedFare] = useState<{ id: number; price: string; currency: string }>({
    id: 0,
    price: '0.00',
    currency: '',
  });

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [recordCount, setRecordCount] = useState(0);
  const [pageSize, _setPageSize] = useState(20);

  const [isLoadingFlights, setIsLoadingFlights] = useState(true);
  const [flightsResponse, setFlightsRespone] = useState<StupidFuckingHack>({ 0: [] });
  const [flightSearchArgs, setFlightSearchArgs] = useState<FlightsRequestPayload[]>([]);

  const handleBack = () => navigate(routes.flights.index, { replace: true });
  const handleEdit = () => setOpenEdit((prevState) => !prevState);
  const handleTune = () => setOpenFilter((prevState) => !prevState);

  const handleBookFare = useCallback(
    (id: number) => () => {
      mutateConfirmFare(id);
    },
    [],
  );

  const handleModalClose = () => {
    setSelectedFare({ id: 0, price: '0.00', currency: '' });
  };

  const mergeItineraries = (itineraries: StupidFuckingHack) =>
    Object.values(itineraries).reduce((p, c) => [...p, ...c]);

  const fetchFlights = useCallback(
    (nextPage?: number) => {
      if (isConnected) {
        setIsLoadingFlights(true);
        sendMessage(
          'flights:get',
          JSON.stringify({
            legs: flightSearchArgs,
            // TODO: Modificar cuando el form tenga estas features dinamicas (adults, children, infants, etc)
            adults: 1,
            children: 0,
            infants: 0,
            currentPage: nextPage || page,
            pageSize,
          }),
        );
      }
    },
    [isConnected, flightSearchArgs, page, pageSize],
  );

  const handleReachedBottom = () => {
    if (page < pageCount) {
      setPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchFlights(nextPage);
        return nextPage;
      });
    }
  };

  useRockBottom(handleReachedBottom);

  useEffect(() => {
    const flightSearchParams = [
      {
        departure: queryParams.origin,
        arrival: queryParams.destination,
        dateFrom: dayjs(queryParams.initialDate).format('YYYY-MM-DD'),
      },
    ];

    if (queryParams.endDate) {
      flightSearchParams.push({
        departure: queryParams.destination,
        arrival: queryParams.origin,
        dateFrom: dayjs(queryParams.endDate).format('YYYY-MM-DD'),
      });
    }

    setFlightSearchArgs(flightSearchParams);

    if (queryParams) {
      fetchFlights();
    }
  }, [isConnected, queryParams]);

  useEffect(() => {
    socketRef.current?.on('flights:get:request:error', (err) => {
      console.error('flights:get:request:error', err);
      setIsLoadingFlights(false);
    });
  }, [isConnected, flightSearchArgs, socketRef.current, isLoadingFlights]);

  useEffect(() => {
    socketRef.current?.on('flights:get:request:success', (msg) => {
      const data = JSON.parse(msg);

      const { items, count, pages, currentPage: responseCurrentPage } = data;

      setPage(responseCurrentPage);
      setPageCount(pages);
      setRecordCount(count);

      setFlightsRespone((prev) => ({ ...prev, [responseCurrentPage]: items }));

      setIsLoadingFlights(false);
    });
  }, [socketRef.current]);

  useEffect(() => {
    socketRef.current?.on('flights:providers:request:success', (message) => {
      if (isConnected) {
        setIsLoadingFlights(true);
        sendMessage('flights:get', message);
      }
    });
  }, [isConnected, socketRef.current]);

  useEffect(() => {
    if (!isFareConfirmLoading && !isFareConfirmError && fareConfirmData?.data.body.message) {
      const bookingId = fareConfirmData.data.body.message;
      const path = routes.flights.booking.replace(':id', bookingId.toString());
      navigate(path);
    }
  }, [isFareConfirmLoading, isFareConfirmError, fareConfirmData]);

  useEffect(() => {
    if (isFareConfirmError) {
      // TODO: invalidar query y re-fetch tarifas una vez pase todo a RQ, actulmente esto es una basura.
      handleModalClose();
      fetchFlights();
    }
  }, [isFareConfirmError]);

  // TODO:
  // - portar MobileAvailability (que esta acoplado con Hotels)
  //   y hacerlo mas generico, reusable.
  // - Retocar los estilos superponiendo el design sobre UI
  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Modal
        sx={{ overflowY: 'scroll' }}
        open={Boolean(selectedFare.id) && !isFareConfirmLoading}
        onClose={handleModalClose}
        aria-labelledby={t('flights.availabilityLayout.flightDetail')}
      >
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          px={theme.spacing(1)}
          mt={theme.spacing(10)}
          maxWidth="498px"
        >
          <Box>
            <FareSummary title={t('flights.availabilityLayout.flightDetail')} fareId={selectedFare.id} />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt="-20px"
            mb={theme.spacing(20)}
            p={theme.spacing(2)}
            sx={{
              backgroundColor: theme.palette.paper.dark,
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
            }}
          >
            <Typography component="span" variant="h6" color={theme.palette.primary.dark}>
              {selectedFare.currency} {parseFloat(selectedFare.price).toFixed()}
            </Typography>
            <Button
              disabled={isFareConfirmLoading}
              variant="contained"
              size="large"
              onClick={handleBookFare(selectedFare.id)}
            >
              {t('flights.cardPackage.reserveButtonText')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box justifyContent="center">
        <MobileAvailabilityLayout
          title={
            <Box display="flex" flexDirection="column" flex="1 0 0" p={1}>
              <Box display="flex" gap={1}>
                <Typography variant="h6" color="primary.contrastText">
                  {queryParams.origin}
                </Typography>
                <Box mt="-2px">
                  {/* TODO: Multi-destination UI */}
                  {queryParams.endDate ? <SwapHoriz fontSize="large" /> : <ArrowRightAlt fontSize="large" />}
                </Box>
                <Typography variant="h6" color="primary.contrastText">
                  {queryParams.destination}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} alignSelf="stretch">
                <Box display="flex" gap={1}>
                  <DateRange />
                  <Typography variant="subtitle1" color="contrastText">
                    {dayjs(queryParams.initialDate).format('D MMM')}.
                    {queryParams.endDate ? ` - ${dayjs(queryParams.endDate).format('D MMM')}` : undefined}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'primary.contrastText' }} />
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Person />
                  <Typography variant="body2" color="contrastText">
                    {/* TODO: */}
                    {queryParams.adults || 1}
                  </Typography>
                </Box>
              </Box>
            </Box>
          }
          onBack={handleBack}
          onEdit={handleEdit}
          openEdit={openEdit}
          openFilter={openFilter}
          edit={
            <Box display="flex" flexDirection="column">
              <BackToolbar title={t('change-me')} handleBack={handleEdit} />
            </Box>
          }
          filter={null}
        >
          <Avatar
            aria-label={t('flights.availabilityLayout.avatarAriaLabel')}
            variant="circular"
            sx={{ background: 'none', color: 'primary.dark', border: '2px solid', marginRight: theme.spacing(2) }}
          >
            <Flight />
          </Avatar>
          <Box flexGrow={1}>
            {isLoadingFlights ? (
              <Skeleton height={40} width={100} />
            ) : (
              <Typography flex="1 0 0" variant="body1" color="text.primary">
                {recordCount}{' '}
                {t('flights.availabilityLayout.result', { count: mergeItineraries(flightsResponse).length })}
              </Typography>
            )}
          </Box>
          <IconButton aria-label={t('flights.availabilityLayout.tuneFiltersAriaLabel')} onClick={handleTune}>
            <Tune color="primary" />
          </IconButton>
        </MobileAvailabilityLayout>
      </Box>
      <Box justifyContent="center" px={theme.spacing(1)}>
        <PriceTrend />
      </Box>
      <Box justifyContent="center">
        {mergeItineraries(flightsResponse).length > 0 && (
          <List sx={{ bgcolor: 'background.paper', padding: 1 }}>
            {mergeItineraries(flightsResponse).map(({ id: itineraryId, price, legs, currency }) => {
              const hardcodedIsRandomlyRecommended = Math.floor(Math.random() * 2) > 0;
              return (
                <ListItem key={itineraryId} sx={{ padding: 0, marginBottom: '1rem' }}>
                  <CustomCard elevation={4} onClick={() => setSelectedFare({ id: itineraryId, price, currency })}>
                    <CardHeader
                      sx={{ padding: 0 }}
                      avatar={
                        <Box>
                          {hardcodedIsRandomlyRecommended && (
                            <Chip
                              avatar={
                                <ThumbUp
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: theme.palette.chips.recommended.text,
                                  }}
                                />
                              }
                              label={t('flights.availabilityCard.recommendedLabel')}
                              size="medium"
                              style={{
                                backgroundColor: theme.palette.chips.recommended.background,
                                color: theme.palette.text.primary,
                                margin: theme.spacing(1),
                              }}
                            />
                          )}
                        </Box>
                      }
                      action={
                        <Box sx={{ paddingY: 1, paddingX: 2 }}>
                          <IconButton aria-label="itinerary-options">
                            <MoreVert />
                          </IconButton>
                        </Box>
                      }
                    />

                    <CardContent sx={{ padding: 0 }}>
                      {legs.map(
                        ({
                          id: legId,
                          departureAirport,
                          arrivalAirport,
                          segments,
                          airline,
                          legNumber,
                          departureTime,
                          arrivalTime,
                          durationLabel,
                        }) => (
                          <Box key={legId}>
                            {legNumber > 1 && (
                              <Box key={`nights-${legId}`} display="flex" justifyContent="center">
                                <Divider sx={{ width: '90%' }}>
                                  <Box display="flex" alignItems="center">
                                    <Box p={0.5}>
                                      <Hotel
                                        fontSize="small"
                                        sx={{ color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))' }}
                                      />
                                    </Box>
                                    <Box p={0.5}>
                                      <Typography sx={{ fontSize: 12 }} color="text.secondary" gutterBottom>
                                        {'32 nights in Miami'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Divider>
                              </Box>
                            )}

                            <Box key={legId} display="flex" flexDirection="column">
                              <Box
                                display="flex"
                                justifyContent="space-evenly"
                                alignItems="center"
                                flexWrap="nowrap"
                                my={theme.spacing(2)}
                              >
                                <Box>
                                  {/* TODO: get from leg data */}
                                  {/* TODO: update to match figma/MUI */}
                                  <img
                                    style={{
                                      maxWidth: 50,
                                    }}
                                    src={returnAirlineLogoUrl(airline)}
                                  />
                                </Box>
                                <Box display="flex" flexDirection="column" alignItems="start">
                                  <Box width="100%" textAlign="right">
                                    <Typography variant="body2" component="span" color="text.secondary">
                                      {dayjs(departureTime).format('DD/MMM')}
                                    </Typography>
                                  </Box>
                                  <Box width="100%" textAlign="right">
                                    <Typography variant="h6" component="span">
                                      {departureAirport}
                                    </Typography>
                                  </Box>
                                  <Box width="100%" textAlign="right">
                                    <Typography variant="body2" component="span" color="text.secondary">
                                      {dayjs(departureTime).format('HH:mm')} hs
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box
                                  display="flex"
                                  rowGap={theme.spacing(1)}
                                  flexDirection="column"
                                  alignItems="center"
                                >
                                  <Box mb="0.5rem">
                                    <Chip
                                      label={durationLabel}
                                      size="small"
                                      sx={{
                                        backgroundColor: theme.palette.field.main,
                                        color: theme.palette.primary.main,
                                      }}
                                    />
                                  </Box>
                                  <StopsIndicator stops={segments - 1} />
                                  <Box>
                                    <Typography color={theme.palette.primary.main} variant="caption">
                                      {segments > 1
                                        ? `${segments - 1} ${t('flights.cardPackage.stop', { count: segments - 1 })}`
                                        : t('flights.cardPackage.direct')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box display="flex" flexDirection="column" alignItems="start">
                                  <Box>
                                    <Typography variant="body2" component="span" color="text.secondary">
                                      {dayjs(arrivalTime).format('DD/MMM')}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="h6" component="span">
                                      {arrivalAirport}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" component="span" color="text.secondary">
                                      {dayjs(arrivalTime).format('HH:mm')} hs
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box>
                                  {/* TODO: hidratar desde la data de api una vez sepamos como parsear
                                  la data de equipaje de starlings/kiwi. */}
                                  <LuggageIndicator
                                    medium={Boolean(Math.floor(Math.random() * 2))}
                                    large={Boolean(Math.floor(Math.random() * 2))}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ),
                      )}
                    </CardContent>

                    <CardActions sx={{ background: 'var(--primary-states-hover, rgba(74, 20, 140, 0.04))' }}>
                      <Box display="flex" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Typography component="span" variant="h6" color={theme.palette.primary.dark} textAlign="start">
                          {currency} {parseFloat(price).toFixed()}
                        </Typography>
                        <Button
                          disabled={isFareConfirmLoading}
                          variant="contained"
                          size="small"
                          onClick={handleBookFare(itineraryId)}
                        >
                          {t('flights.cardPackage.reserveButtonText')}
                        </Button>
                      </Box>
                    </CardActions>
                  </CustomCard>
                </ListItem>
              );
            })}
          </List>
        )}
        {isLoadingFlights && recordCount === 0 && (
          <Box
            p={theme.spacing(10)}
            display="flex"
            gap={2}
            marginBottom={theme.spacing(18)}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <CircularProgress color="primary" />
            <Typography variant="body1" textAlign="center">
              {t('flights.availabilityLayout.loadingFlightsLegend')}
            </Typography>
          </Box>
        )}

        {isLoadingFlights && recordCount > 0 && (
          <Box
            p={theme.spacing(2)}
            display="flex"
            gap={2}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <CircularProgress color="primary" />
            <Typography variant="body1" textAlign="center">
              {t('flights.availabilityLayout.loadingMoreFlights')}
            </Typography>
          </Box>
        )}
        {!isLoadingFlights && recordCount === 0 && (
          <Box
            mt={theme.spacing(6)}
            p={theme.spacing(10)}
            display="flex"
            gap={2}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Typography variant="body1" textAlign="center">
              {t('flights.availabilityLayout.noFares')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
